<form class="patient-form" [formGroup]="patientForm" autocomplete="off">
  <h1>{{ pageTitle }}</h1>
  <p><span class="required-asterisk">*</span> = required</p>
  <div class="fieldset-container">
    <p-fieldset legend="Identification" [toggleable]="true">
      <div class="form-container">
        <emr-input
          type="text"
          label="First Name"
          [control]="firstNameFormControl"
          [required]="true"
        ></emr-input>
        <emr-input type="text" label="Middle Name" [control]="middleNameFormControl"></emr-input>
        <emr-input
          type="text"
          label="Last Name"
          [control]="lastNameFormControl"
          [required]="true"
        ></emr-input>
      </div>
      <div class="form-container">
        <emr-input
          type="text"
          label="Medical Record Number"
          [control]="idFormControl"
          [bottomSpace]="true"
          [required]="true"
        ></emr-input>
        @if (isCreateType) {
          <button
            pButton
            pRipple
            class="mrn-btn"
            type="button"
            label="Generate MRN"
            class="p-button-outlined"
            (click)="generateMRN()"
          ></button>
        }
      </div>
      <!-- Errors for MRN -->
      @if (
        isCreateType && idFormControl.invalid && (idFormControl.dirty || idFormControl.touched)
        ) {
        <span
          >
          <!-- <span *ngIf="idFormControl.errors?.['required']" class="error-msg"> MRN is required. </span> -->
          @if (idFormControl.errors?.['uniqueEntry']) {
            <span class="error-msg">
              This MRN is already in use by another patient.
            </span>
          }
        </span>
      }
    </p-fieldset>
  </div>

  <div class="fieldset-container" formGroupName="generalInfo">
    <p-fieldset legend="General Information" [toggleable]="true">
      <div class="form-container">
        <emr-input
          type="select"
          label="Type"
          id="Type *"
          [control]="patientTypeFormControl"
          [options]="patientTypeOptions"
          [bottomSpace]="true"
          [required]="true"
        ></emr-input>

        <!-- Age for non-pediatrics-->
        @if (!patientTypeIsPediatric) {
          <emr-input
            label="Age (years)"
            hint="Number from 0-130"
            [control]="ageFormControl"
            [min]="minMeasurement"
            [max]="maxAge"
            [required]="true"
            type="number"
          ></emr-input>
        }
        <!-- Age for pediatrics-->
        @if (patientTypeIsPediatric) {
          <emr-input
            type="text"
            label="Age (free text)"
            [control]="ageFormControl"
            [bottomSpace]="true"
            [required]="true"
          ></emr-input>
        }

        <emr-input
          type="select"
          label="Sex"
          id="Sex *"
          [control]="sexFormControl"
          [options]="sexOptions"
          [bottomSpace]="true"
          [required]="true"
        ></emr-input>
      </div>
      <div class="form-container">
        <div class="dob-row">
          <label>Date of Birth</label>
          <div class="dob-selects">
            <div>
              <emr-input
                type="select"
                label="Month"
                id="Month *"
                [control]="dobMonthFormControl"
                [options]="monthOptions"
                [bottomSpace]="true"
                [required]="true"
              ></emr-input>
            </div>
            <div>
              <emr-input
                type="select"
                label="Day"
                id="Day *"
                [control]="dobDayFormControl"
                [options]="dayOptions"
                [bottomSpace]="true"
                [required]="true"
              ></emr-input>
            </div>
          </div>
        </div>
      </div>
      <div class="form-container">
        <emr-input
          type="decimal"
          label="Weight (kilograms)"
          id="Weight (kilograms) *"
          hint="Number from 0-300"
          [control]="weightFormControl"
          [min]="minMeasurement"
          [max]="maxWeight"
          [required]="true"
        ></emr-input>
        <emr-input
          type="number"
          label="Height (feet)"
          id="Height (feet) *"
          hint="Number from 0-10"
          [control]="heightFeetFormControl"
          [min]="minMeasurement"
          [max]="maxFeet"
          [required]="true"
        ></emr-input>
        <emr-input
          type="number"
          label="Height (inches)"
          id="Height (inches) *"
          hint="Number from 0-11"
          [control]="heightInchesFormControl"
          [min]="minMeasurement"
          [max]="maxInches"
          [required]="true"
        ></emr-input>
      </div>
      <div class="form-container">
        <emr-input
          type="select"
          label="Code Status"
          id="Code Status *"
          [control]="codeStatusFormControl"
          [options]="codeStatusOptions"
          [required]="true"
        ></emr-input>
        <emr-input
          type="text"
          label="Isolation"
          [control]="isolationFormControl"
          [required]="true"
          />
      </div>
      <div class="form-container">
        <emr-input type="text" label="Room #" [control]="roomNumberFormControl" />
        <emr-input
          type="text"
          label="Location (e.g., PCP office, School Nurse Office, ICU, etc.)"
          [control]="locationFormControl"
          />
      </div>
    </p-fieldset>
  </div>

  @if (isCreateType) {
    <div class="btn-container">
      <div>
        <button pButton pRipple type="button" label="Randomize" (click)="randomizeForm()"></button>
      </div>
      <p>
        <b>Note:</b> This button is a simple randomizer. Be sure to check the randomized values and
        change any that may not line up with others.
      </p>
    </div>
  }

  <div class="fieldset-container">
    <p-fieldset legend="Medical Information" [toggleable]="true">
      <p class="med-info-note">
        <b>Note:</b> If the patient does not have any interdisciplinary team individuals, active
        medical problems, or allergies, make sure to delete the form boxes under that specific
        category in order to submit the form.
      </p>

      <div class="med-info">
        <h2>Interdisciplinary Team</h2>
        <ng-container formArrayName="interdisciplinaryTeam">
          @for (providerFormGroup of interdisciplinaryTeamFormArray.controls; track providerFormGroup; let i = $index) {
            <ng-container
              [formGroupName]="i"
              >
              <div class="med-info-form-row">
                <div class="med-info-input-div">
                  <label for="providerSpecialization">Provider {{ i + 1 }} Specialization</label>
                  <input
                    id="providerSpecialization"
                    type="text"
                    pInputText
                    formControlName="specialization"
                    />
                </div>
                <div class="med-info-input-div">
                  <label for="providerName">Provider {{ i + 1 }} Name</label>
                  <input id="providerName" type="text" pInputText formControlName="name" />
                </div>
                <div class="med-info-input-div">
                  <label for="providerTitle">Provider {{ i + 1 }} Title</label>
                  <input id="providerTitle" type="text" pInputText formControlName="title" />
                </div>
                <button
                  pButton
                  pRipple
                  type="button"
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-danger p-button-text"
                  (click)="deleteFormGroup(i, 'team')"
                ></button>
              </div>
            </ng-container>
          }
          <button
            pButton
            pRipple
            type="button"
            icon="pi pi-plus"
            class="p-button-rounded"
            (click)="addEmptyFormGroup('team')"
          ></button>
        </ng-container>
      </div>

      <p-divider></p-divider>

      <div class="med-info">
        <h2>Active Medical Problems</h2>
        <ng-container formArrayName="activeMedicalProblems">
          @for (medProblemFormGroup of activeMedProblemsFormArray.controls; track medProblemFormGroup; let i = $index) {
            <ng-container
              [formGroupName]="i"
              >
              <div class="med-info-form-row">
                <div class="med-info-input-div">
                  <label for="medicalProblem">Medical Problem {{ i + 1 }} Name</label>
                  <input id="medicalProblem" type="text" pInputText formControlName="name" />
                </div>
                <div class="med-info-input-div">
                  <label for="altName">Enter alternate name (if any)</label>
                  <input id="altName" type="text" pInputText formControlName="altName" />
                </div>
                <button
                  pButton
                  pRipple
                  type="button"
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-danger p-button-text"
                  (click)="deleteFormGroup(i, 'problem')"
                ></button>
              </div>
            </ng-container>
          }
          <button
            pButton
            pRipple
            type="button"
            icon="pi pi-plus"
            class="p-button-rounded"
            (click)="addEmptyFormGroup('problem')"
          ></button>
        </ng-container>
      </div>

      <p-divider></p-divider>

      <div class="med-info">
        <h2>Allergies</h2>
        <ng-container formArrayName="allergies">
          @for (allergyFormGroup of allergiesFormArray.controls; track allergyFormGroup; let i = $index) {
            <ng-container
              [formGroupName]="i"
              >
              <div class="med-info-form-row">
                <div class="med-info-input-div">
                  <label for="medicalProblem">Allergy {{ i + 1 }}</label>
                  <input id="medicalProblem" type="text" pInputText formControlName="name" />
                </div>
                <div class="med-info-input-div">
                  <label for="altName">Enter alternate name (if any)</label>
                  <input id="altName" type="text" pInputText formControlName="altName" />
                </div>
                <button
                  pButton
                  pRipple
                  type="button"
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-danger p-button-text"
                  (click)="deleteFormGroup(i, 'allergy')"
                ></button>
              </div>
            </ng-container>
          }
          <button
            pButton
            pRipple
            type="button"
            icon="pi pi-plus"
            class="p-button-rounded"
            (click)="addEmptyFormGroup('allergy')"
          ></button>
        </ng-container>
      </div>
    </p-fieldset>
  </div>
  <div class="fieldset-container">
    <p-fieldset legend="Upload Profile Picture" [toggleable]="true">
      <p-fileUpload
        accept="image/*"
        [maxFileSize]="500000"
        chooseLabel="Browse Files"
        [showUploadButton]="false"
        [showCancelButton]="false"
        (onSelect)="onFileChange($event)"
        (onRemove)="onFileChange($event)"
      ></p-fileUpload>
    </p-fieldset>
  </div>
  <div class="submit-btn-container">
    <button
      pButton
      [label]="submitButtonLabel"
      type="button"
      (click)="upsertPatient()"
      [loading]="didSubmit"
    ></button>
  </div>
</form>
